import {
  WEB_AR_ENGINE_PACKAGE,
  WebArError,
  type WebArEvent,
  type WebArFailureCode,
  type WebArLaunchPayload,
  type WebArState
} from "./contracts.js";
import type { SignedLaunchResolver } from "./launch.js";

export interface WebArCapabilityPort {
  detect(): Promise<{ readonly supported: boolean; readonly reason?: string }>;
}

export interface CameraAuthorization {
  release(): void;
}

export interface WebArCameraPort {
  authorize(signal: AbortSignal): Promise<CameraAuthorization>;
}

export interface LoadedWebArEngine {
  readonly runtime: unknown;
}

export interface WebArEnginePort {
  load(signal: AbortSignal): Promise<LoadedWebArEngine>;
}

export interface VerifiedWebArModel {
  readonly resource: unknown;
  readonly sha256: string;
  readonly byteLength: number;
  dispose(): void;
}

export interface WebArModelPort {
  load(model: WebArLaunchPayload["model"], signal: AbortSignal): Promise<VerifiedWebArModel>;
}

export interface WebArRuntimeSession {
  setScale(scale: number): number;
  reposition(): void;
  stop(): void | Promise<void>;
}

export interface WebArRuntimePort {
  start(input: {
    readonly engine: LoadedWebArEngine;
    readonly model: VerifiedWebArModel;
    readonly launch: WebArLaunchPayload;
    readonly canvas: HTMLCanvasElement;
    readonly signal: AbortSignal;
    readonly onPlacement: (reason: "initial" | "tap" | "recenter") => void;
    readonly onScale: (scale: number) => void;
    readonly onFailure: (error: unknown) => void;
  }): Promise<WebArRuntimeSession>;
}

export interface WebArViewPort {
  readonly canvas: HTMLCanvasElement;
  bindStart(listener: () => void): () => void;
  bindExit(listener: () => void): () => void;
  bindScale(listener: (scale: number) => void): () => void;
  bindReposition(listener: () => void): () => void;
  setScale(scale: number): void;
  render(input: {
    readonly state: WebArState;
    readonly message: string;
    readonly errorCode?: WebArFailureCode;
    readonly scale?: WebArLaunchPayload["scale"];
  }): void;
  dispose(): void;
}

export interface WebArEventPort {
  emit(event: WebArEvent): void;
}

export interface WebArReturnPort {
  returnToStorefront3d(origin: string | undefined): void;
}

export interface WebArControllerDependencies {
  readonly launches: SignedLaunchResolver;
  readonly capabilities: WebArCapabilityPort;
  readonly camera: WebArCameraPort;
  readonly engine: WebArEnginePort;
  readonly models: WebArModelPort;
  readonly runtime: WebArRuntimePort;
  readonly view: WebArViewPort;
  readonly events: WebArEventPort;
  readonly returns: WebArReturnPort;
  readonly now?: () => Date;
}

export type WebArStartResult =
  | { readonly status: "started"; readonly launchGrantId: string }
  | { readonly status: "failed"; readonly code: WebArFailureCode }
  | { readonly status: "ignored" };

const STATE_MESSAGES: Record<WebArState, string> = {
  idle: "Opening AR…",
  resolving_launch: "Opening AR…",
  requesting_camera: "Allow camera access to continue.",
  loading_engine: "Starting AR…",
  loading_model: "Loading your configured product…",
  starting_tracking: "Move your phone slowly to find the floor.",
  active: "Tap a surface to move the product. Pinch or use the scale control to resize it.",
  error: "AR is not available right now. You can continue in the 3D configurator.",
  exited: "Returning to the 3D configurator…"
};

function normalizeError(error: unknown, stage: WebArState): WebArError {
  if (error instanceof WebArError) return error;
  if (error instanceof DOMException && error.name === "AbortError") {
    return new WebArError("cancelled", "The AR launch was cancelled.", true, { cause: error });
  }
  const fallbackCode: WebArFailureCode =
    stage === "requesting_camera" ? "camera_denied" :
    stage === "loading_engine" ? "engine_load_failed" :
    stage === "loading_model" ? "model_load_failed" : "runtime_failed";
  return new WebArError(fallbackCode, "The AR experience could not be started.", true, {
    cause: error
  });
}

export class WebArFallbackController {
  readonly #dependencies: WebArControllerDependencies;
  readonly #unbind: Array<() => void>;
  #state: WebArState = "idle";
  #launch: WebArLaunchPayload | undefined;
  #model: VerifiedWebArModel | undefined;
  #session: WebArRuntimeSession | undefined;
  #camera: CameraAuthorization | undefined;
  #abort: AbortController | undefined;
  #inFlight: Promise<WebArStartResult> | undefined;
  #disposed = false;

  constructor(dependencies: WebArControllerDependencies) {
    this.#dependencies = dependencies;
    this.#unbind = [
      dependencies.view.bindStart(() => { void this.start(); }),
      dependencies.view.bindExit(() => { void this.exit(); }),
      dependencies.view.bindScale((scale) => this.setScale(scale)),
      dependencies.view.bindReposition(() => this.reposition())
    ];
    this.#render("idle");
  }

  getState(): WebArState {
    return this.#state;
  }

  start(): Promise<WebArStartResult> {
    if (this.#disposed || this.#state === "active") {
      return Promise.resolve({ status: "ignored" });
    }
    if (this.#inFlight) return this.#inFlight;
    this.#inFlight = this.#start().finally(() => {
      this.#inFlight = undefined;
    });
    return this.#inFlight;
  }

  async #start(): Promise<WebArStartResult> {
    this.#emit({ type: "intent_received", at: this.#timestamp() });
    this.#abort?.abort();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    let stage: WebArState = "resolving_launch";

    try {
      if (!this.#launch) {
        this.#render(stage);
        this.#launch = await this.#dependencies.launches.resolve(signal);
        this.#throwIfAborted(signal);
        this.#emit({
          type: "launch_resolved",
          at: this.#timestamp(),
          launchGrantId: this.#launch.launchGrantId,
          releaseId: this.#launch.release.releaseId,
          compositionId: this.#launch.composition.compositionId
        });
      }
      if (Date.parse(this.#launch.expiresAt) <= this.#now().getTime()) {
        throw new WebArError("expired", "The AR launch has expired.", true);
      }

      const capability = await this.#dependencies.capabilities.detect();
      if (!capability.supported) {
        throw new WebArError("unsupported", "This browser cannot run the WebAR fallback.", false);
      }

      stage = "requesting_camera";
      this.#render(stage);
      this.#camera = await this.#dependencies.camera.authorize(signal);
      this.#throwIfAborted(signal);
      this.#emit({ type: "camera_granted", at: this.#timestamp() });

      stage = "loading_engine";
      this.#render(stage);
      const engine = await this.#dependencies.engine.load(signal);
      this.#throwIfAborted(signal);
      this.#emit({
        type: "engine_loaded",
        at: this.#timestamp(),
        package: WEB_AR_ENGINE_PACKAGE
      });

      stage = "loading_model";
      this.#render(stage);
      this.#model?.dispose();
      this.#model = await this.#dependencies.models.load(this.#launch.model, signal);
      this.#throwIfAborted(signal);
      this.#emit({
        type: "model_verified",
        at: this.#timestamp(),
        sha256: this.#model.sha256,
        byteLength: this.#model.byteLength
      });

      stage = "starting_tracking";
      this.#render(stage);
      this.#session = await this.#dependencies.runtime.start({
        engine,
        model: this.#model,
        launch: this.#launch,
        canvas: this.#dependencies.view.canvas,
        signal,
        onPlacement: (reason) => this.#emit({
          type: "placement_changed",
          at: this.#timestamp(),
          reason
        }),
        onScale: (scale) => {
          this.#dependencies.view.setScale(scale);
          this.#emit({
            type: "scale_changed",
            at: this.#timestamp(),
            scale
          });
        },
        onFailure: (error) => { void this.#handleRuntimeFailure(error); }
      });
      this.#throwIfAborted(signal);
      this.#camera.release();
      this.#camera = undefined;
      this.#emit({ type: "tracking_started", at: this.#timestamp() });
      this.#render("active", undefined, this.#launch.scale);
      return { status: "started", launchGrantId: this.#launch.launchGrantId };
    } catch (error) {
      const failure = normalizeError(error, stage);
      this.#camera?.release();
      this.#camera = undefined;
      if (this.#session) {
        await this.#session.stop();
        this.#session = undefined;
      }
      this.#model?.dispose();
      this.#model = undefined;
      if (this.#disposed || this.#state === "exited") {
        return { status: "failed", code: "cancelled" };
      }
      this.#emit({
        type: "failure",
        at: this.#timestamp(),
        code: failure.code,
        recoverable: failure.recoverable,
        stage
      });
      this.#render("error", failure.code);
      return { status: "failed", code: failure.code };
    }
  }

  setScale(scale: number): void {
    if (this.#state !== "active" || !this.#session) return;
    this.#session.setScale(scale);
  }

  reposition(): void {
    if (this.#state !== "active" || !this.#session) return;
    this.#session.reposition();
  }

  async exit(): Promise<void> {
    if (this.#state === "exited" || this.#disposed) return;
    this.#abort?.abort();
    this.#render("exited");
    this.#camera?.release();
    this.#camera = undefined;
    if (this.#session) {
      await this.#session.stop();
      this.#session = undefined;
    }
    this.#model?.dispose();
    this.#model = undefined;
    this.#emit({
      type: "fallback_exit",
      at: this.#timestamp(),
      destination: "storefront_3d"
    });
    this.#dependencies.returns.returnToStorefront3d(this.#launch?.origin);
  }

  async dispose(): Promise<void> {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#abort?.abort();
    this.#camera?.release();
    if (this.#session) await this.#session.stop();
    this.#model?.dispose();
    for (const unbind of this.#unbind.splice(0)) unbind();
    this.#dependencies.view.dispose();
  }

  async #handleRuntimeFailure(error: unknown): Promise<void> {
    if (this.#state !== "active") return;
    const failure = normalizeError(error, "active");
    const session = this.#session;
    this.#session = undefined;
    this.#emit({
      type: "failure",
      at: this.#timestamp(),
      code: failure.code,
      recoverable: failure.recoverable,
      stage: "active"
    });
    this.#render("error", failure.code);
    if (session) await session.stop();
    this.#model?.dispose();
    this.#model = undefined;
  }

  #render(
    state: WebArState,
    errorCode?: WebArFailureCode,
    scale?: WebArLaunchPayload["scale"]
  ): void {
    this.#state = state;
    this.#dependencies.view.render({
      state,
      message: STATE_MESSAGES[state],
      ...(errorCode === undefined ? {} : { errorCode }),
      ...(scale === undefined ? {} : { scale })
    });
  }

  #emit(event: WebArEvent): void {
    this.#dependencies.events.emit(event);
  }

  #now(): Date {
    return this.#dependencies.now?.() ?? new Date();
  }

  #timestamp(): string {
    return this.#now().toISOString();
  }

  #throwIfAborted(signal: AbortSignal): void {
    if (signal.aborted || this.#disposed || this.#state === "exited") {
      throw new WebArError("cancelled", "The AR launch was cancelled.", true);
    }
  }
}
